import React, { useState } from 'react';
import {
  Grid,
  Card,
  Paper,
  CardActions,
  Button,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Contact } from './simulator.slice';
import { useTheme } from '@mui/material';
import { CcfTypography, CcfTextField } from '@nice-ccf/ui-controls';
import simulatorStyles from './simulator.styles';
import classes from './simulator.module.scss';
import { useSelector } from 'react-redux';
import { getCopilotConfig } from '@nice-ccf/ui-components';
import { BroadcastChannel } from 'broadcast-channel';

interface ConfigField {
  key: string;
  type: 'switch' | 'text' | 'select';
  label: string;
  dependentField?: string;
  inputProps?: {
    max?: number;
    min?: number;
    step?: number;
    type?: string;
  };
  options?: string[];
  dependsOn?: string;
}

const RESPONSE_TONES = [
  'Formal',
  'Casual',
  'Confident',
  'Empathetic',
  'Instructive'
];

const CONFIG_FIELDS: ConfigField[] = [
  {
    key: 'generativeResponseTone',
    type: 'select',
    label: 'Response Tone',
    options: RESPONSE_TONES,
  },
  {
    key: 'enableSentiment',
    type: 'switch',
    label: 'Sentiment',
  },
  {
    key: 'sentimentResponseLength',
    type: 'text',
    label: 'Response Length',
    dependsOn: 'enableSentiment',
    inputProps: { max: 5, min: 1 },
  },
  {
    key: 'realTimeSummary',
    type: 'switch',
    label: 'Real-time summary',
  },
  {
    key: 'realTimeSummaryResponseLength',
    type: 'text',
    label: 'Response Length',
    dependsOn: 'realTimeSummary',
    inputProps: { max: 5, min: 1 },
  },
  {
    key: 'finalSummary',
    type: 'switch',
    label: 'Final Summary',
  },
  {
    key: 'finalSummaryResponseLength',
    type: 'text',
    label: 'Response Length',
    dependsOn: 'finalSummary',
    inputProps: { max: 5, min: 1 },
  },
  {
    key: 'nextBestResponse',
    type: 'switch',
    label: 'Next-Best Response',
  },
  {
    key: 'nextBestResponseLength',
    type: 'text',
    label: 'Response Length',
    dependsOn: 'nextBestResponse',
    inputProps: { max: 5, min: 1 },
  },
  {
    key: 'nextBestNumberOfResponse',
    type: 'text',
    label: '# Of Response',
    dependsOn: 'nextBestResponse',
    inputProps: { max: 5, min: 1, type: 'number' },
  },
  {
    key: 'knowledgeBaseArticles',
    type: 'switch',
    label: 'Knowledge Base Articles',
  },
  {
    key: 'threshold',
    type: 'text',
    label: 'Threshold',
    inputProps: { max: 1, min: 0, step: 0.05, type: 'number' },
  },
  {
    key: 'knowledgeBaseArticlesResponseLength',
    type: 'text',
    label: 'Response Length',
    dependsOn: 'knowledgeBaseArticles',
    inputProps: { max: 5, min: 1 },
  },
  {
    key: 'knowledgeBaseWebLinks',
    type: 'switch',
    label: 'Web links',
  },
  {
    key: 'knowledgeBaseMaximumWebLinks',
    type: 'text',
    label: 'Maximum',
    dependsOn: 'knowledgeBaseWebLinks',
    inputProps: { max: 5, min: 1, type: 'number' },
  },
  {
    key: 'knowledgeBaseImages',
    type: 'switch',
    label: 'Images',
  },
  {
    key: 'knowledgeBaseMaximumImages',
    type: 'text',
    label: 'Maximum',
    dependsOn: 'knowledgeBaseImages',
    inputProps: { max: 5, min: 1, type: 'number' },
  },
  {
    key: 'knowledgeBaseProcessSteps',
    type: 'switch',
    label: 'Process Steps',
  },
  {
    key: 'knowledgeBaseMaximumSteps',
    type: 'text',
    label: 'Maximum',
    dependsOn: 'knowledgeBaseProcessSteps',
    inputProps: { max: 10, min: 1, type: 'number' },
  },
  {
    key: 'salesOffers',
    type: 'switch',
    label: 'Sales Offers',
  },
  {
    key: 'threshold_sales',
    type: 'text',
    label: 'Sales Threshold',
    inputProps: { max: 1, min: 0, step: 0.05, type: 'number' },
  },
  {
    key: 'compliance',
    type: 'switch',
    label: 'Compliance',
  },
  {
    key: 'threshold_compliance',
    type: 'text',
    label: 'Compliance Threshold',
    inputProps: { max: 1, min: 0, step: 0.05, type: 'number' },
  },
  {
    key: 'backAndForthLiveConvo',
    type: 'switch',
    label: 'Back-and-forth Live Convo',
  },
  {
    key: 'backAndForthLiveConvoLength',
    type: 'text',
    label: 'Maximum',
    dependsOn: 'backAndForthLiveConvo',
    inputProps: { max: 10, min: 2, type: 'number' },
  },
];

  /**
   * used to handle Config field in simulator
   * @param props - ConfigFieldProps
   * @example ` <CopilotConfig />`
   */
const ConfigField: React.FC<{
  field: ConfigField;
  value: any;
  onChange: (e: any, key: string) => void;
  disabled?: boolean;
  simStyles: any;
}> = ({ field, value, onChange, disabled, simStyles }) => {
  switch (field.type) {
    case 'select':
      return (
        <FormControl fullWidth>
          <InputLabel>{field.label}</InputLabel>
          <Select
            value={value}
            label={field.label}
            onChange={(e) => onChange(e, field.key)}
          >
            {field.options?.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    case 'switch':
      return (
        <FormControlLabel
          label={field.label}
          labelPlacement="start"
          style={simStyles.simLabelStyles}
          control={
            <Switch
              name={field.key}
              color="primary"
              checked={value}
              style={{ marginLeft: 0 }}
              onChange={(e) => onChange(e, field.key)}
            />
          }
        />
      );
    case 'text':
      return (
        <CcfTextField
          id="outlined-basic"
          className={classes.input}
          label={field.label}
          variant="outlined"
          value={value}
          onChange={(e) => onChange(e, field.key)}
          InputLabelProps={{ shrink: true }}
          inputProps={field.inputProps}
          disabled={disabled}
        />
      );
    default:
      return null;
  }
};

/**
 * To show the copilot configuration
 * @param props - it will take selected contact detail, and onchange handler
 * @returns
 * @example
 */
export default function CopilotConfig(props: {
  selectedContact: Contact;
  handleButtonClick: () => void;
  buttonText: string;
}) {
  const theme = useTheme();
  const simStyles = simulatorStyles(theme);
  const copilotConfiguration: any = useSelector(getCopilotConfig);
  const [localConfiguration, setLocalConfiguration] = useState(copilotConfiguration);

    /**
   * used to show Copilot Config Input change in simulator
   * @param props - CopilotConfigProps
   * @example ` <CopilotConfig />`
   */
  const handleInputChange = (e: any, key: string) => {
    setLocalConfiguration((prevConfig: any) => ({
      ...prevConfig,
      [key]: e.target.value,
    }));
  };

    /**
   * used to handle Config changes in  the simulator
   * @param props - CopilotConfigProps
   * @example ` <CopilotConfig />`
   */
  const handleSwitchChange = (e: any, key: string) => {
    setLocalConfiguration((prevConfig: any) => ({
      ...prevConfig,
      [key]: e.target.checked,
    }));
  };

    /**
   * used to handle Save changes in the simulator
   * @param props - CopilotConfigProps
   * @example ` <CopilotConfig />`
   */
  const handleSaveClick = async () => {
    const channel: BroadcastChannel<any> = new BroadcastChannel('simulator_channel');
    await channel.postMessage({
      type: 'saveCopilotConfig',
      data: localConfiguration,
    });
    await channel.close();
  };

  return (
    <Grid className={classes.simulatorColumn}>
      <Paper elevation={3} className={classes.simulatorPaperWithFullheight}>
        <Card className={classes.heightInherit}>
          <CcfTypography
            className={classes.txtChatMsg}
            style={{ marginTop: '20px', marginLeft: '15px' }}
          >
            COPILOT PROFILE SETTINGS
          </CcfTypography>
          <CardContent>
            <form
              className={`${classes.root} ${classes.simulatorForm}`}
              noValidate
              autoComplete="off"
            >
              {CONFIG_FIELDS.map((field) => (
                <ConfigField
                  key={field.key}
                  field={field}
                  value={localConfiguration[field.key]}
                  onChange={field.type === 'switch' ? handleSwitchChange : handleInputChange}
                  disabled={field.dependsOn ? !localConfiguration[field.dependsOn] : false}
                  simStyles={simStyles}
                />
              ))}
            </form>
          </CardContent>
          <CardActions className={classes.simulatorSaveButton}>
            <Button
              size="large"
              variant="contained"
              color="primary"
              onClick={handleSaveClick}
            >
              Apply
            </Button>
          </CardActions>
        </Card>
      </Paper>
    </Grid>
  );
}